import { X } from "lucide-react";
import { FilterIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar } from "../../ui/calendar";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";
import { CalendarIcon as LucideCalendarIcon } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { Badge } from "../../ui/badge";

const Filter = ({
  column,
  onServerFilter = null,
  clearFiltersTriggered = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const columnFilterValue = column.getFilterValue();
  const facetedUniqueValues = column.getFacetedUniqueValues();
  const filterType = column.columnDef.meta?.filterType;
  const columnInfo = column.columnDef.meta?.columnInfo;

  // Always use the original filterType (based on column type) for UI logic
  // The filter_type from API is only for server-side filtering operations, not UI rendering
  const effectiveFilterType = filterType;

  // Server-side filter state for glid field
  const [serverFilterValue, setServerFilterValue] = useState("");
  const serverFilterTimeoutRef = useRef(null);
  const lastServerFilterValueRef = useRef("");

  // Server-side range filter state
  const [serverMinValue, setServerMinValue] = useState("");
  const [serverMaxValue, setServerMaxValue] = useState("");
  const rangeFilterTimeoutRef = useRef(null);
  const lastRangeValuesRef = useRef({ min: "", max: "" });

  // Server-side dropdown filter state
  const [serverDropdownValue, setServerDropdownValue] = useState("");
  const dropdownFilterTimeoutRef = useRef(null);
  const lastDropdownValueRef = useRef("");

  // Track user interaction to avoid logging on mount
  const hasUserInteracted = useRef(false);

  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);

  // Refs for calendar popover content
  const minCalendarContentRef = useRef(null);
  const maxCalendarContentRef = useRef(null);

  // Clear searchTerm when filter is cleared externally
  useEffect(() => {
    if (columnFilterValue === undefined) {
      setSearchTerm("");
      setMinDate(null);
      setMaxDate(null);
    }
  }, [columnFilterValue]);

  // Clear all server-side filter states when clearFiltersTriggered changes
  useEffect(() => {
    if (clearFiltersTriggered > 0) {
      setServerFilterValue("");
      setServerMinValue("");
      setServerMaxValue("");
      setServerDropdownValue("");
      setSearchTerm("");
      setMinDate(null);
      setMaxDate(null);

      // Reset the last values to prevent duplicate API calls
      lastServerFilterValueRef.current = "";
      lastRangeValuesRef.current = { min: "", max: "" };
      lastDropdownValueRef.current = "";
    }
  }, [clearFiltersTriggered]);

  // Server-side filter effect for text fields
  useEffect(() => {
    if (
      onServerFilter &&
      columnInfo?.filter_type === "text" &&
      hasUserInteracted.current &&
      lastServerFilterValueRef.current !== serverFilterValue
    ) {
      // Clear existing timeout
      if (serverFilterTimeoutRef.current) {
        clearTimeout(serverFilterTimeoutRef.current);
      }

      // Set new timeout for 600ms debounce
      serverFilterTimeoutRef.current = setTimeout(() => {
        // Update the last value after API call to prevent duplicate calls
        lastServerFilterValueRef.current = serverFilterValue;

        if (serverFilterValue === "") {
          // Clear the filter
          onServerFilter(column.id, "", "like");
        } else {
          // Apply the filter
          onServerFilter(column.id, serverFilterValue, "like");
        }
      }, 600);
    }

    // Cleanup timeout on unmount
    return () => {
      if (serverFilterTimeoutRef.current) {
        clearTimeout(serverFilterTimeoutRef.current);
      }
    };
  }, [serverFilterValue, onServerFilter, column.id, columnInfo?.filter_type]);

  // Server-side range filter effect
  useEffect(() => {
    if (
      onServerFilter &&
      columnInfo?.filter_type === "range" &&
      hasUserInteracted.current &&
      (lastRangeValuesRef.current.min !== serverMinValue ||
        lastRangeValuesRef.current.max !== serverMaxValue)
    ) {
      // Clear existing timeout
      if (rangeFilterTimeoutRef.current) {
        clearTimeout(rangeFilterTimeoutRef.current);
      }

      // Set new timeout for 600ms debounce
      rangeFilterTimeoutRef.current = setTimeout(() => {
        const filters = [];

        // Add min filter if value exists
        if (serverMinValue && serverMinValue.trim() !== "") {
          filters.push({
            field: column.id,
            op: ">=",
            value: serverMinValue.trim(),
          });
        }

        // Add max filter if value exists
        if (serverMaxValue && serverMaxValue.trim() !== "") {
          filters.push({
            field: column.id,
            op: "<=",
            value: serverMaxValue.trim(),
          });
        }

        // Update the last values after API call to prevent duplicate calls
        lastRangeValuesRef.current = {
          min: serverMinValue,
          max: serverMaxValue,
        };

        // Send all range filters for this field (empty array if no filters)
        onServerFilter(column.id, filters, "range");
      }, 600);
    }

    // Cleanup timeout on unmount
    return () => {
      if (rangeFilterTimeoutRef.current) {
        clearTimeout(rangeFilterTimeoutRef.current);
      }
    };
  }, [
    serverMinValue,
    serverMaxValue,
    onServerFilter,
    column.id,
    columnInfo?.filter_type,
  ]);

  // Server-side dropdown filter effect
  useEffect(() => {
    if (
      onServerFilter &&
      (columnInfo?.type === "rich_dropdown" ||
        columnInfo?.filter_type === "dropdown" ||
        columnInfo?.filter_type === "select") &&
      hasUserInteracted.current &&
      lastDropdownValueRef.current !== serverDropdownValue
    ) {
      // Clear existing timeout
      if (dropdownFilterTimeoutRef.current) {
        clearTimeout(dropdownFilterTimeoutRef.current);
      }

      // Set new timeout for 600ms debounce
      dropdownFilterTimeoutRef.current = setTimeout(() => {
        lastDropdownValueRef.current = serverDropdownValue;

        if (serverDropdownValue === "") {
          // Clear the filter
          onServerFilter(column.id, "", "==");
        } else {
          // Apply the filter with == operator
          onServerFilter(column.id, serverDropdownValue, "==");
        }
      }, 600);
    }

    // Cleanup timeout on unmount
    return () => {
      if (dropdownFilterTimeoutRef.current) {
        clearTimeout(dropdownFilterTimeoutRef.current);
      }
    };
  }, [
    serverDropdownValue,
    onServerFilter,
    column.id,
    columnInfo?.filter_type,
    columnInfo?.type,
  ]);

  // Server-side date range filter effect
  useEffect(() => {
    if (
      onServerFilter &&
      (columnInfo?.filter_type === "date" ||
        columnInfo?.filter_type === "datetime" ||
        columnInfo?.filter_type === "date_range" ||
        (columnInfo?.filter_type === "range" &&
          (columnInfo?.type === "date" || columnInfo?.type === "datetime"))) &&
      hasUserInteracted.current
    ) {
      // Use the same range timeout ref as numeric ranges
      if (rangeFilterTimeoutRef.current) {
        clearTimeout(rangeFilterTimeoutRef.current);
      }

      // Set new timeout for 600ms debounce
      rangeFilterTimeoutRef.current = setTimeout(() => {
        const filters = [];

        // Add min date filter if value exists
        if (minDate) {
          filters.push({
            field: column.id,
            op: ">=",
            value: format(minDate, "yyyy-MM-dd"),
          });
        }

        // Add max date filter if value exists
        if (maxDate) {
          filters.push({
            field: column.id,
            op: "<=",
            value: format(maxDate, "yyyy-MM-dd"),
          });
        }

        // Send all date range filters for this field (empty array if no filters)
        onServerFilter(column.id, filters, "range");
      }, 600);
    }

    // Cleanup timeout on unmount
    return () => {
      if (rangeFilterTimeoutRef.current) {
        clearTimeout(rangeFilterTimeoutRef.current);
      }
    };
  }, [minDate, maxDate, onServerFilter, column.id, columnInfo?.filter_type]);

  useEffect(() => {
    // Only load dates from column filter value for client-side filtering
    if (!onServerFilter) {
      if (
        (filterType === "range" || filterType === "date_range") &&
        (columnInfo?.type === "datetime" || columnInfo?.type === "date") &&
        columnFilterValue
      ) {
        if (columnFilterValue[0]) {
          const parsedMin = parseISO(columnFilterValue[0]);
          if (isValid(parsedMin)) setMinDate(parsedMin);
        } else {
          setMinDate(null);
        }
        if (columnFilterValue[1]) {
          const parsedMax = parseISO(columnFilterValue[1]);
          if (isValid(parsedMax)) setMaxDate(parsedMax);
        } else {
          setMaxDate(null);
        }
      } else if (
        (filterType !== "range" && filterType !== "date_range") ||
        (columnInfo?.type !== "datetime" && columnInfo?.type !== "date")
      ) {
        setMinDate(null);
        setMaxDate(null);
      }
    }
  }, [columnFilterValue, filterType, columnInfo, onServerFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the click is on the button that opens/closes the main dropdown, let it toggle.
      if (buttonRef.current && buttonRef.current.contains(event.target)) {
        return;
      }

      // If the click is inside a portalled calendar popover content
      if (
        (minCalendarContentRef.current &&
          minCalendarContentRef.current.contains(event.target)) ||
        (maxCalendarContentRef.current &&
          maxCalendarContentRef.current.contains(event.target))
      ) {
        // The click is intended for the calendar. Let the calendar's Popover handle its own closing.
        // The main filter dropdown should remain open.
        return;
      }

      // If the click is inside the main filter dropdown (and not portalled content)
      // This check needs to be after the calendar content check if dropdownRef contains PopoverTriggers
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }

      // If none of the above, the click is truly outside all relevant UI for the filter.
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]); // Depends on isOpen to add/remove listener

  // Calculate dropdown position
  const getDropdownPosition = () => {
    if (!buttonRef.current) return {};
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
    };
  };

  // Helper function to check if any filter has a value
  const hasFilterValue = () => {
    // Check client-side filter
    if (columnFilterValue) {
      return true;
    }

    // Check server-side filters
    if (onServerFilter) {
      switch (columnInfo?.filter_type) {
        case "range":
          // For date fields with range filter type, check date values
          if (columnInfo?.type === "date" || columnInfo?.type === "datetime") {
            return minDate || maxDate;
          }
          // For numeric range filters, check if min or max has a value
          return (
            (serverMinValue && serverMinValue.trim() !== "") ||
            (serverMaxValue && serverMaxValue.trim() !== "")
          );
        case "date":
        case "datetime":
        case "date_range":
          // For date filters, check if min or max date is set
          return minDate || maxDate;
        case "select":
        case "dropdown":
          // For dropdown filters, check if server dropdown value has content
          return serverDropdownValue && serverDropdownValue.trim() !== "";
        case "text":
        default:
          // For text filters and others, check if server filter value has content
          return serverFilterValue && serverFilterValue.trim() !== "";
      }
    }

    return false;
  };
  const filterHasValue = hasFilterValue();

  const clearFilter = () => {
    hasUserInteracted.current = true;
    column.setFilterValue(undefined);
    setSearchTerm("");

    // Clear server-side filter states
    if (onServerFilter) {
      setServerFilterValue("");
      setServerMinValue("");
      setServerMaxValue("");
      setServerDropdownValue("");
    }

    // Clear date filter states
    setMinDate(null);
    setMaxDate(null);
  };

  // For range filters (numeric values)
  if (filterType === "range") {
    if (columnInfo?.type === "datetime") {
      const handleEmptyFilter = () => {
        column.setFilterValue(["empty"]);
        setIsOpen(false);
      };

      const handleImmediateLaunchFilter = () => {
        column.setFilterValue(["immediate"]);
        setIsOpen(false);
      };

      const clearDateFilter = () => {
        setMinDate(null);
        setMaxDate(null);
        column.setFilterValue(undefined);
      };

      // Helper to get active filter label
      const getActiveFilterLabel = () => {
        if (!columnFilterValue) return null;
        const [filterType, maxDate] = columnFilterValue;

        if (filterType === "empty") return "Empty Values";
        if (filterType === "immediate") return "Immediate Launch";
        if (filterType && !maxDate)
          return format(new Date(filterType), "MM/dd/yyyy");
        if (filterType && maxDate) return "Date Range";
        return null;
      };

      const activeFilterLabel = getActiveFilterLabel();

      const handleMinDateSelect = (date) => {
        setMinDate(date);
        column.setFilterValue((old) => [
          date ? format(date, "yyyy-MM-dd") : undefined,
          old?.[1],
        ]);
      };

      const handleMaxDateSelect = (date) => {
        setMaxDate(date);
        column.setFilterValue((old) => [
          old?.[0],
          date ? format(date, "yyyy-MM-dd") : undefined,
        ]);
      };

      return (
        <div className="relative">
          <div className="flex items-center gap-1">
            <button
              ref={buttonRef}
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "p-1.5 rounded-md transition-all duration-200 flex items-center gap-1.5",
                columnFilterValue
                  ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                  : "text-gray-500 hover:bg-gray-100"
              )}
            >
              <FilterIcon className="w-3.5 h-3.5" />
              {activeFilterLabel && (
                <>
                  <span className="text-xs font-medium">
                    {activeFilterLabel}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearDateFilter();
                    }}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </>
              )}
            </button>
          </div>

          {isOpen && (
            <div
              ref={dropdownRef}
              className="absolute top-full mt-1 bg-white border rounded-lg shadow-lg p-3 z-50 min-w-[280px]"
            >
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Date Filter
                  </span>
                  {columnFilterValue && (
                    <button
                      onClick={clearDateFilter}
                      className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Clear
                    </button>
                  )}
                </div>
                {/* Special filters for datetime columns */}
                <div className="flex flex-col gap-1.5">
                  <Button
                    variant={
                      columnFilterValue?.[0] === "empty" ? "default" : "ghost"
                    }
                    onClick={handleEmptyFilter}
                    className="w-full justify-start text-sm h-8"
                  >
                    Show Empty Values
                  </Button>
                  {columnInfo.key === "forecasted_launch_date" && (
                    <Button
                      variant={
                        columnFilterValue?.[0] === "immediate"
                          ? "default"
                          : "ghost"
                      }
                      onClick={handleImmediateLaunchFilter}
                      className="w-full justify-start text-sm h-8"
                    >
                      Show Immediate Launch
                    </Button>
                  )}
                  <div className="border-t my-1"></div>
                </div>
                <div className="space-y-2 flex flex-col">
                  <p className="text-xs text-gray-500 font-medium">
                    Date Range
                  </p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full justify-start text-left font-normal h-8",
                          !minDate && "text-muted-foreground"
                        )}
                      >
                        <LucideCalendarIcon className="mr-2 h-4 w-4" />
                        {minDate ? (
                          format(minDate, "PPP")
                        ) : (
                          <span>Starting at</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      ref={minCalendarContentRef}
                      className="w-auto p-0"
                    >
                      <Calendar
                        mode="single"
                        selected={minDate}
                        onSelect={handleMinDateSelect}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full justify-start text-left font-normal h-8",
                          !maxDate && "text-muted-foreground"
                        )}
                      >
                        <LucideCalendarIcon className="mr-2 h-4 w-4" />
                        {maxDate ? (
                          format(maxDate, "PPP")
                        ) : (
                          <span>Ending at</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      ref={maxCalendarContentRef}
                      className="w-auto p-0"
                    >
                      <Calendar
                        mode="single"
                        selected={maxDate}
                        onSelect={handleMaxDateSelect}
                        disabled={(date) => minDate && date < minDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      // Existing numeric range filter
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className={`p-1 rounded-md hover:bg-gray-100 transition-colors ${
            filterHasValue ? "!text-blue-600" : "text-gray-500"
          }`}
        >
          <FilterIcon className="w-4 h-4" />
        </button>

        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute top-full mt-1 bg-white border rounded-md shadow-lg p-2 z-50 min-w-[280px]"
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Range Filter</span>
                {(onServerFilter
                  ? serverMinValue || serverMaxValue
                  : columnFilterValue) && (
                  <button
                    onClick={() => {
                      hasUserInteracted.current = true;
                      if (onServerFilter) {
                        setServerMinValue("");
                        setServerMaxValue("");
                      } else {
                        clearFilter();
                      }
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    clear
                  </button>
                )}
              </div>
              <input
                type="number"
                value={
                  onServerFilter ? serverMinValue : columnFilterValue?.[0] ?? ""
                }
                onChange={(e) => {
                  const val = e.target.value;
                  hasUserInteracted.current = true;

                  if (onServerFilter) {
                    // Server-side filtering
                    setServerMinValue(val);
                  } else {
                    // Client-side filtering
                    column.setFilterValue((old) => [
                      val ? parseFloat(val) : undefined,
                      old?.[1],
                    ]);
                  }
                }}
                placeholder={onServerFilter ? "Min (server-side)" : "Min"}
                className="w-full border rounded-md px-2 py-1 text-sm"
              />
              <input
                type="number"
                value={
                  onServerFilter ? serverMaxValue : columnFilterValue?.[1] ?? ""
                }
                onChange={(e) => {
                  const val = e.target.value;
                  hasUserInteracted.current = true;

                  if (onServerFilter) {
                    // Server-side filtering
                    setServerMaxValue(val);
                  } else {
                    // Client-side filtering
                    column.setFilterValue((old) => [
                      old?.[0],
                      val ? parseFloat(val) : undefined,
                    ]);
                  }
                }}
                placeholder={onServerFilter ? "Max (server-side)" : "Max"}
                className="w-full border rounded-md px-2 py-1 text-sm"
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // For select filters (boolean, enum values)
  if (filterType === "select") {
    const uniqueValues = Array.from(facetedUniqueValues.keys())
      .filter((value) => value != null && value !== "")
      .sort((a, b) => String(a).localeCompare(String(b)));

    const options = [
      { value: "", label: "All" },
      ...uniqueValues.map((value) => {
        if (columnInfo?.type === "bool") {
          const normalizedValue = value.toString().toLowerCase();
          const isTrueValue =
            normalizedValue === "true" || normalizedValue === "1";
          const isFalseValue =
            normalizedValue === "false" || normalizedValue === "0";

          return {
            value: isTrueValue ? "true" : isFalseValue ? "false" : value,
            label: isTrueValue
              ? columnInfo.display_true
              : isFalseValue
              ? columnInfo.display_false
              : columnInfo.display_null,
          };
        }
        return { value: value, label: value };
      }),
    ];

    return (
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className={`p-1 rounded-md hover:bg-gray-100 transition-colors ${
            filterHasValue ? "!text-blue-600" : "text-gray-500"
          }`}
        >
          <FilterIcon className="w-4 h-4" />
        </button>

        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute top-full mt-1 bg-white border rounded-md shadow-lg p-2 z-50 min-w-[280px]"
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Select Filter</span>
                {columnFilterValue && (
                  <button
                    onClick={clearFilter}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    clear
                  </button>
                )}
              </div>
              <select
                value={
                  onServerFilter ? serverDropdownValue : columnFilterValue ?? ""
                }
                onChange={(e) => {
                  hasUserInteracted.current = true;
                  let value = e.target.value;

                  if (columnInfo?.type === "bool") {
                    value = value === "" ? undefined : value === "true";
                  }

                  if (onServerFilter) {
                    // Server-side filtering for select filters
                    setServerDropdownValue(value || "");
                  } else {
                    // Client-side filtering
                    column.setFilterValue(value);
                  }
                }}
                className="w-full border rounded-md px-2 py-1 text-sm"
              >
                {options.map(({ value, label }) => (
                  <option key={String(value)} value={String(value)}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    );
  }

  // if (columnInfo.display === "Interest") {
  //   debugger;
  // }

  const uniqueValues = Array.from(facetedUniqueValues.keys())
    .filter((value) => {
      if (value == null || value === "") return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object" && value !== null) {
        // For rich_dropdown type
        if (columnInfo?.type === "rich_dropdown") {
          return value.glid != null;
        }
        // For regular dropdown type
        return value.value != null;
      }
      return value?.length > 0;
    })
    .flatMap((value) => {
      // Handle rich_dropdown type
      if (columnInfo?.type === "rich_dropdown") {
        if (typeof value === "object" && value !== null) {
          return [
            {
              id: value.glid,
              name: value.title,
              description: value.description,
              image: value.image,
              label: `${value.glid} - ${value.title}`,
              raw: value,
            },
          ];
        }
      }
      // Handle regular dropdown type
      if (typeof value === "object" && value !== null) {
        if (Array.isArray(value)) {
          // Handle multiple selection case
          return value.map((v) => ({
            id: v.value,
            name: v.display,
            label: v.display,
            raw: v,
          }));
        }
        // Handle single selection case
        return [
          {
            id: value.value,
            name: value.display,
            label: value.display,
            raw: value,
          },
        ];
      }
      // Fallback for primitive values
      return [
        {
          id: value,
          name: value,
          label: String(value),
          raw: value,
        },
      ];
    })
    .filter(
      (v, i, self) =>
        self.findIndex((x) => x.id === v.id && x.name === v.name) === i
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  const filteredValues = uniqueValues.filter(
    ({ id, name, description }) =>
      (id &&
        id
          .toString()
          .toLowerCase()
          .includes((searchTerm || "").toLowerCase())) ||
      (name &&
        name
          .toString()
          .toLowerCase()
          .includes((searchTerm || "").toLowerCase())) ||
      (description &&
        description
          .toString()
          .toLowerCase()
          .includes((searchTerm || "").toLowerCase()))
  );

  // Dynamic filter renderer based on filter type
  const renderFilterByType = () => {
    // For server-side filtering, prioritize backend filter_type
    const filterTypeToUse = onServerFilter
      ? columnInfo?.filter_type || effectiveFilterType
      : effectiveFilterType;

    switch (filterTypeToUse) {
      case "text":
        return renderTextFilter();
      case "range":
        return renderRangeFilter();
      case "select":
      case "dropdown":
        return renderDropdownFilter();
      case "date":
      case "datetime":
      case "date_range": // Add support for date_range filter type
        return renderDateRangeFilter();
      default:
        // Fallback to original logic for backward compatibility
        return renderDefaultFilter();
    }
  };

  // Text filter component
  const renderTextFilter = () => {
    if (!isOpen) return null;

    const dropdownStyle = {
      position: "absolute",
      ...getDropdownPosition(),
      backgroundColor: "white",
      border: "1px solid #e2e8f0",
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      padding: "0.5rem",
      zIndex: 9999,
      minWidth: "280px",
      maxWidth: "350px",
    };

    const content = (
      <div ref={dropdownRef} style={dropdownStyle}>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Text Filter</span>
            {(onServerFilter ? serverFilterValue : columnFilterValue) && (
              <button
                onClick={clearFilter}
                className="text-gray-400 hover:text-gray-600"
              >
                clear
              </button>
            )}
          </div>
          <div className="relative">
            <input
              type="text"
              value={onServerFilter ? serverFilterValue : searchTerm}
              onChange={(e) => {
                hasUserInteracted.current = true;
                if (onServerFilter) {
                  setServerFilterValue(e.target.value);
                } else {
                  setSearchTerm(e.target.value);
                  column.setFilterValue(e.target.value || undefined);
                }
              }}
              placeholder={`Filter ${columnInfo?.display || column.id} (${
                onServerFilter ? "server-side" : "client-side"
              })`}
              className="w-full border rounded-md pl-2 pr-8 py-1 text-sm"
            />
            {(onServerFilter ? serverFilterValue : searchTerm) && (
              <button
                onClick={clearFilter}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Show dropdown list of unique values for text filters - only for client-side */}
          {!onServerFilter && (
            <div className="mt-1 max-h-48 overflow-y-auto flex flex-col gap-1">
              {(searchTerm ? filteredValues : uniqueValues).map(
                ({ id, name, label, raw }) => (
                  <button
                    key={id}
                    onClick={() =>
                      handleOptionSelect(
                        columnInfo?.type === "rich_dropdown"
                          ? raw
                          : { id, name, label, raw }
                      )
                    }
                    className="w-full text-left px-2 py-1 text-sm hover:bg-gray-100 rounded-md"
                  >
                    {columnInfo?.type === "rich_dropdown" && (
                      <div className="font-mono text-xs text-gray-500">
                        {id}
                      </div>
                    )}
                    <div>{name}</div>
                  </button>
                )
              )}
            </div>
          )}
          {/* Show message for server-side text filtering */}
          {onServerFilter && (
            <div className="mt-1 p-2 text-xs text-gray-500 text-center">
              Enter text to filter server-side
            </div>
          )}
        </div>
      </div>
    );

    return createPortal(content, document.body);
  };

  // Range filter component
  const renderRangeFilter = () => {
    if (!isOpen) return null;

    const dropdownStyle = {
      position: "absolute",
      ...getDropdownPosition(),
      backgroundColor: "white",
      border: "1px solid #e2e8f0",
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      padding: "0.5rem",
      zIndex: 9999,
      minWidth: "280px",
      maxWidth: "350px",
    };

    const content = (
      <div ref={dropdownRef} style={dropdownStyle}>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Range Filter</span>
            {(serverMinValue || serverMaxValue) && (
              <button
                onClick={clearFilter}
                className="text-gray-400 hover:text-gray-600"
              >
                clear
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-500">Min</label>
              <input
                type="number"
                value={serverMinValue}
                onChange={(e) => {
                  hasUserInteracted.current = true;
                  setServerMinValue(e.target.value);
                }}
                placeholder="Min value"
                className="w-full border rounded-md px-2 py-1 text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500">Max</label>
              <input
                type="number"
                value={serverMaxValue}
                onChange={(e) => {
                  hasUserInteracted.current = true;
                  setServerMaxValue(e.target.value);
                }}
                placeholder="Max value"
                className="w-full border rounded-md px-2 py-1 text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    );

    return createPortal(content, document.body);
  };

  // Dropdown filter component
  const renderDropdownFilter = () => {
    if (!isOpen) return null;

    const dropdownStyle = {
      position: "absolute",
      ...getDropdownPosition(),
      backgroundColor: "white",
      border: "1px solid #e2e8f0",
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      padding: "0.5rem",
      zIndex: 9999,
      minWidth: "280px",
      maxWidth: "350px",
    };

    const content = (
      <div ref={dropdownRef} style={dropdownStyle}>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Dropdown Filter</span>
            {(onServerFilter ? serverDropdownValue : columnFilterValue) && (
              <button
                onClick={clearFilter}
                className="text-gray-400 hover:text-gray-600"
              >
                clear
              </button>
            )}
          </div>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                hasUserInteracted.current = true;
                setSearchTerm(e.target.value);
              }}
              placeholder={`Search ${
                columnInfo?.display || column.id
              } options...`}
              className="w-full border rounded-md pl-2 pr-8 py-1 text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="mt-1 max-h-48 overflow-y-auto flex flex-col gap-1">
            {(searchTerm ? filteredValues : uniqueValues).map(
              ({ id, name, label, raw }) => (
                <button
                  key={id}
                  onClick={() =>
                    handleOptionSelect(
                      columnInfo?.type === "rich_dropdown"
                        ? raw
                        : { id, name, label, raw }
                    )
                  }
                  className="w-full text-left px-2 py-1 text-sm hover:bg-gray-100 rounded-md"
                >
                  {columnInfo?.type === "rich_dropdown" && (
                    <div className="font-mono text-xs text-gray-500">{id}</div>
                  )}
                  <div>{name}</div>
                </button>
              )
            )}
          </div>
        </div>
      </div>
    );

    return createPortal(content, document.body);
  };

  // Date range filter component
  const renderDateRangeFilter = () => {
    if (!isOpen) return null;

    const dropdownStyle = {
      position: "absolute",
      ...getDropdownPosition(),
      backgroundColor: "white",
      border: "1px solid #e2e8f0",
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      padding: "0.5rem",
      zIndex: 9999,
      minWidth: "350px",
      maxWidth: "400px",
    };

    const content = (
      <div ref={dropdownRef} style={dropdownStyle}>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Date Range Filter</span>
            {(minDate || maxDate) && (
              <button
                onClick={clearFilter}
                className="text-gray-400 hover:text-gray-600"
              >
                clear
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                From Date
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !minDate && "text-muted-foreground"
                    )}
                  >
                    <LucideCalendarIcon className="mr-2 h-4 w-4" />
                    {minDate ? format(minDate, "PPP") : "Pick start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  ref={minCalendarContentRef}
                  className="w-auto p-0 z-[10000]"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={minDate}
                    onSelect={(date) => {
                      hasUserInteracted.current = true;
                      setMinDate(date);
                      // Only store in column filter value for client-side filtering
                      if (!onServerFilter) {
                        column.setFilterValue((old) => [
                          date ? format(date, "yyyy-MM-dd") : undefined,
                          old?.[1],
                        ]);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                To Date
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !maxDate && "text-muted-foreground"
                    )}
                  >
                    <LucideCalendarIcon className="mr-2 h-4 w-4" />
                    {maxDate ? format(maxDate, "PPP") : "Pick end date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  ref={maxCalendarContentRef}
                  className="w-auto p-0 z-[10000]"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={maxDate}
                    onSelect={(date) => {
                      hasUserInteracted.current = true;
                      setMaxDate(date);
                      // Only store in column filter value for client-side filtering
                      if (!onServerFilter) {
                        column.setFilterValue((old) => [
                          old?.[0],
                          date ? format(date, "yyyy-MM-dd") : undefined,
                        ]);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>
    );

    return createPortal(content, document.body);
  };

  // Default filter (original complex logic for backward compatibility)
  const renderDefaultFilter = () => {
    return renderDropdownContent();
  };

  const handleOptionSelect = (valueObj) => {
    hasUserInteracted.current = true;

    if (valueObj && typeof valueObj === "object") {
      if (columnInfo?.type === "rich_dropdown") {
        setSearchTerm(valueObj.title || valueObj.name);

        if (onServerFilter) {
          // Server-side filtering: use glid for filtering

          setServerDropdownValue(valueObj.glid);
        } else {
          // Client-side filtering
          column.setFilterValue(valueObj.glid);
        }
      } else if (valueObj.raw) {
        // For regular dropdowns, use value
        const displayValue =
          valueObj?.raw?.display || valueObj.raw || valueObj.name;
        setSearchTerm(displayValue);

        if (onServerFilter) {
          // Server-side filtering: use the actual value
          setServerDropdownValue(
            valueObj?.raw?.value || valueObj.raw || valueObj.id
          );
        } else {
          // Client-side filtering
          column.setFilterValue(displayValue);
        }
      } else {
        // For other object types
        setSearchTerm(valueObj.name || valueObj.label);

        if (onServerFilter) {
          // Server-side filtering: use the actual value
          setServerDropdownValue(valueObj.raw || valueObj.id);
        } else {
          // Client-side filtering
          column.setFilterValue(valueObj.raw || valueObj.id);
        }
      }
    } else {
      // For primitive values
      setSearchTerm(String(valueObj));

      if (onServerFilter) {
        // Server-side filtering
        setServerDropdownValue(String(valueObj));
      } else {
        // Client-side filtering
        column.setFilterValue(valueObj);
      }
    }

    setIsOpen(false);
  };

  const renderDropdownContent = () => {
    if (!isOpen) return null;

    const dropdownStyle = {
      position: "absolute",
      ...getDropdownPosition(),
      backgroundColor: "white",
      border: "1px solid #e2e8f0",
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      padding: "0.5rem",
      zIndex: 9999,
      minWidth: "280px",
      maxWidth: "350px",
    };

    const content = (
      <div ref={dropdownRef} style={dropdownStyle}>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {columnInfo?.type === "rich_dropdown" || filterType === "dropdown"
                ? "Dropdown Filter"
                : "Text Filter"}
            </span>
            {(columnFilterValue ||
              (onServerFilter &&
                (columnInfo?.type === "rich_dropdown" ||
                filterType === "dropdown"
                  ? serverDropdownValue
                  : serverFilterValue))) && (
              <button
                onClick={clearFilter}
                className="text-gray-400 hover:text-gray-600"
              >
                clear
              </button>
            )}
          </div>
          <div className="relative">
            <input
              type="text"
              value={
                onServerFilter && filterType !== "range"
                  ? columnInfo?.type === "rich_dropdown" ||
                    filterType === "dropdown"
                    ? searchTerm // For dropdowns, show the display name, not the value
                    : serverFilterValue
                  : searchTerm
              }
              onChange={(e) => {
                hasUserInteracted.current = true;

                if (onServerFilter && filterType !== "range") {
                  if (
                    columnInfo?.type === "rich_dropdown" ||
                    filterType === "dropdown"
                  ) {
                    // For dropdowns, just update searchTerm for filtering options
                    setSearchTerm(e.target.value);
                  } else {
                    // Server-side filtering for text fields
                    setServerFilterValue(e.target.value);
                  }
                } else {
                  // Client-side filtering for other fields
                  setSearchTerm(e.target.value);
                  column.setFilterValue(e.target.value || undefined);
                }
              }}
              placeholder={
                onServerFilter && filterType !== "range"
                  ? columnInfo?.type === "rich_dropdown" ||
                    filterType === "dropdown"
                    ? `Search ${columnInfo?.display || column.id} options...`
                    : `Filter ${columnInfo?.display || column.id} (server-side)`
                  : `Search (${uniqueValues.length})`
              }
              className="w-full border rounded-md pl-2 pr-8 py-1 text-sm"
            />
            {(onServerFilter && filterType !== "range"
              ? columnInfo?.type === "rich_dropdown" ||
                filterType === "dropdown"
                ? searchTerm
                : serverFilterValue
              : searchTerm) && (
              <button
                onClick={() => {
                  hasUserInteracted.current = true;
                  if (onServerFilter && filterType !== "range") {
                    if (
                      columnInfo?.type === "rich_dropdown" ||
                      filterType === "dropdown"
                    ) {
                      setSearchTerm("");
                      setServerDropdownValue("");
                    } else {
                      setServerFilterValue("");
                    }
                  } else {
                    clearFilter();
                  }
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Hide dropdown suggestions only for text filters with server-side filtering */}
          {!(
            onServerFilter &&
            (columnInfo?.type === "str" || columnInfo?.type === "uri")
          ) && (
            <div className="mt-1 max-h-48 overflow-y-auto flex flex-col gap-1">
              {(searchTerm ? filteredValues : uniqueValues).map(
                ({ id, name, label, raw }) => (
                  <button
                    key={id}
                    onClick={() =>
                      handleOptionSelect(
                        columnInfo?.type === "rich_dropdown"
                          ? raw
                          : { id, name, label, raw }
                      )
                    }
                    className="w-full text-left px-2 py-1 text-sm hover:bg-gray-100 rounded-md"
                  >
                    {columnInfo?.type === "rich_dropdown" && (
                      <div className="font-mono text-xs text-gray-500">
                        {id}
                      </div>
                    )}
                    <div>{name}</div>
                  </button>
                )
              )}
            </div>
          )}
          {/* Show message for server-side text filtering */}
          {onServerFilter && columnInfo?.type === "str" && (
            <div className="mt-1 p-2 text-xs text-gray-500 text-center">
              Enter text to filter server-side
            </div>
          )}
        </div>
      </div>
    );

    return createPortal(content, document.body);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`p-1 rounded-md hover:bg-gray-100 transition-colors ${
          filterHasValue ? "!text-blue-600" : "text-gray-500"
        }`}
      >
        <FilterIcon className="w-4 h-4" />
      </button>
      {renderFilterByType()}
    </div>
  );
};

export default Filter;
